import React, { useState, useEffect } from "react";
import { 
    Card, Row, Col, Button, Spinner, Alert, Table,
    Form, Badge, Pagination, OverlayTrigger, Tooltip,
    ProgressBar
} from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import { formatCurrency, formatDate, formatNumber } from "../utils/formatters";
import { API_BASE_URL } from "../constants/api";
import Footer from "../layouts/Footer";
import Header from "../layouts/Header";


export default function TopClients() {
    const navigate = useNavigate();
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [periode, setPeriode] = useState('mois');
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);
    const [sortConfig, setSortConfig] = useState({ key: 'total_achats', direction: 'desc' });

    useEffect(() => {
        fetchData();
    }, [periode]);

    const fetchData = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/stats.php?action=top_clients&periode=${periode}`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            
            if (!response.ok) throw new Error("Erreur chargement");
            
            const result = await response.json();
            setData(result.data || []);
            setCurrentPage(1);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const getPeriodeLabel = () => {
        switch(periode) {
            case 'mois': return "ce mois";
            case 'trimestre': return "ce trimestre";
            case 'annee': return "cette année";
            default: return "cette période";
        }
    };

    const getPeriodeIcon = () => {
        switch(periode) {
            case 'mois': return "ri-calendar-line";
            case 'trimestre': return "ri-calendar-2-line";
            case 'annee': return "ri-calendar-check-line";
            default: return "ri-calendar-line";
        }
    };

    const sortedData = React.useMemo(() => {
        const sortableData = [...data];
        if (sortConfig.key) {
            sortableData.sort((a, b) => {
                let aVal = a[sortConfig.key];
                let bVal = b[sortConfig.key];
                
                if (sortConfig.key === 'total_achats' || sortConfig.key === 'panier_moyen' || sortConfig.key === 'nb_factures') {
                    aVal = parseFloat(aVal) || 0;
                    bVal = parseFloat(bVal) || 0;
                } else {
                    aVal = aVal?.toString().toLowerCase() || '';
                    bVal = bVal?.toString().toLowerCase() || '';
                }
                
                if (aVal < bVal) {
                    return sortConfig.direction === 'asc' ? -1 : 1;
                }
                if (aVal > bVal) {
                    return sortConfig.direction === 'asc' ? 1 : -1;
                }
                return 0;
            });
        }
        return sortableData;
    }, [data, sortConfig]);

    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = sortedData.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(sortedData.length / itemsPerPage);

    const handleSort = (key) => {
        setSortConfig(prev => ({
            key,
            direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
        }));
    };

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const renderPagination = () => {
        let items = [];
        const maxVisible = 5;
        let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
        let endPage = Math.min(totalPages, startPage + maxVisible - 1);

        if (endPage - startPage + 1 < maxVisible) {
            startPage = Math.max(1, endPage - maxVisible + 1);
        }

        if (startPage > 1) {
            items.push(
                <Pagination.Item key={1} onClick={() => handlePageChange(1)}>
                    1
                </Pagination.Item>
            );
            if (startPage > 2) {
                items.push(<Pagination.Ellipsis key="ellipsis-start" disabled />);
            }
        }

        for (let number = startPage; number <= endPage; number++) {
            items.push(
                <Pagination.Item 
                    key={number} 
                    active={number === currentPage}
                    onClick={() => handlePageChange(number)}
                >
                    {number}
                </Pagination.Item>
            );
        }

        if (endPage < totalPages) {
            if (endPage < totalPages - 1) {
                items.push(<Pagination.Ellipsis key="ellipsis-end" disabled />);
            }
            items.push(
                <Pagination.Item key={totalPages} onClick={() => handlePageChange(totalPages)}>
                    {totalPages}
                </Pagination.Item>
            );
        }

        return items;
    };

    const totalAchats = sortedData.reduce((sum, item) => sum + (parseFloat(item.total_achats) || 0), 0);
    const totalFactures = sortedData.reduce((sum, item) => sum + (parseInt(item.nb_factures) || 0), 0);
    const moyenneAchats = sortedData.length > 0 ? totalAchats / sortedData.length : 0;

    if (loading) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 'calc(100vh - 100px)' }}>
                        <div className="text-center">
                            <div className="spinner-border text-primary mb-3" style={{ width: '3rem', height: '3rem', color: '#21ACCD' }} role="status">
                                <span className="visually-hidden">Chargement...</span>
                            </div>
                            <h5 className="text-muted">Analyse des meilleurs clients...</h5>
                        </div>
                    </div>
                </div>
                <Footer />
            </>
        );
    }

    if (error) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4">
                    <Alert variant="danger" className="border-0 shadow-sm rounded-3">
                        <div className="d-flex align-items-center">
                            <i className="ri-error-warning-line fs-1 me-3 text-danger"></i>
                            <div>
                                <h6 className="mb-1 fw-bold">Erreur de chargement</h6>
                                <p className="mb-0 small">{error}</p>
                                <Button variant="outline-danger" size="sm" className="mt-2 rounded-pill" onClick={fetchData}>
                                    <i className="ri-refresh-line me-1"></i>Réessayer
                                </Button>
                            </div>
                        </div>
                    </Alert>
                </div>
                <Footer />
            </>
        );
    }

    return (
        <>
            <Header />
            <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                <div className="container-fluid">
                    {/* En-tête simplifié */}
                    <div className="d-md-flex align-items-center justify-content-between mb-4">
                        <div>
                            <div className="d-flex align-items-center gap-2 mb-2">
                                <div className="p-2 bg-primary bg-opacity-10 rounded-3">
                                    <i className="ri-group-line fs-20 text-primary" style={{ color: '#21ACCD' }}></i>
                                </div>
                                <div>
                                    <h4 className="main-title mb-0 fw-bold">Top clients</h4>
                                    <p className="text-muted small mb-0">
                                        Meilleurs clients {getPeriodeLabel()} • {sortedData.length} clients
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div className="d-flex gap-2 mt-3 mt-md-0">
                            <Button 
                                variant="outline-secondary" 
                                onClick={() => navigate('/stats/dashboard')}
                                className="rounded-pill px-3 d-flex align-items-center gap-1"
                                size="sm"
                            >
                                <i className="ri-arrow-left-line"></i>
                                Retour
                            </Button>
                        </div>
                    </div>

                    {/* KPI Cards modernes */}
                    <Row className="g-3 mb-4">
                        <Col sm={6} xl={4}>
                            <div className="kpi-card kpi-primary">
                                <div className="kpi-icon">
                                    <i className="ri-money-euro-circle-line fs-24"></i>
                                </div>
                                <div className="kpi-content">
                                    <span className="kpi-label">Total achats</span>
                                    <h3 className="kpi-value">{formatCurrency(totalAchats)}</h3>
                                    <span className="kpi-unit">cumul période</span>
                                </div>
                            </div>
                        </Col>
                        <Col sm={6} xl={4}>
                            <div className="kpi-card kpi-info">
                                <div className="kpi-icon">
                                    <i className="ri-file-list-line fs-24"></i>
                                </div>
                                <div className="kpi-content">
                                    <span className="kpi-label">Total factures</span>
                                    <h3 className="kpi-value">{formatNumber(totalFactures)}</h3>
                                    <span className="kpi-unit">documents</span>
                                </div>
                            </div>
                        </Col>
                        <Col sm={6} xl={4}>
                            <div className="kpi-card kpi-success">
                                <div className="kpi-icon">
                                    <i className="ri-calculator-line fs-24"></i>
                                </div>
                                <div className="kpi-content">
                                    <span className="kpi-label">Achat moyen</span>
                                    <h3 className="kpi-value" style={{ fontSize: '1rem' }}>{formatCurrency(moyenneAchats)}</h3>
                                    <span className="kpi-unit">par client</span>
                                </div>
                            </div>
                        </Col>
                    </Row>

                    {/* Filtres modernes */}
                    <Card className="border-0 shadow-sm rounded-3 mb-4">
                        <Card.Body className="py-3 px-4">
                            <Row className="align-items-end g-3">
                                <Col md={4}>
                                    <Form.Group>
                                        <Form.Label className="fw-semibold text-muted small text-uppercase">
                                            <i className={`${getPeriodeIcon()} me-2`}></i>
                                            Période
                                        </Form.Label>
                                        <Form.Select 
                                            value={periode} 
                                            onChange={(e) => setPeriode(e.target.value)}
                                            className="rounded-pill"
                                        >
                                            <option value="mois">Ce mois</option>
                                            <option value="trimestre">Ce trimestre</option>
                                            <option value="annee">Cette année</option>
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                                <Col md={4}>
                                    <Form.Group>
                                        <Form.Label className="fw-semibold text-muted small text-uppercase">
                                            <i className="ri-list-settings-line me-2"></i>
                                            Affichage
                                        </Form.Label>
                                        <Form.Select 
                                            value={itemsPerPage} 
                                            onChange={(e) => {
                                                setItemsPerPage(parseInt(e.target.value));
                                                setCurrentPage(1);
                                            }}
                                            className="rounded-pill"
                                        >
                                            <option value="5">5 par page</option>
                                            <option value="10">10 par page</option>
                                            <option value="20">20 par page</option>
                                            <option value="50">50 par page</option>
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                                <Col md={4}>
                                    <Button 
                                        variant="primary" 
                                        onClick={fetchData}
                                        className="w-100 rounded-pill py-2"
                                        style={{ background: '#21ACCD', border: 'none' }}
                                    >
                                        <i className="ri-refresh-line me-2"></i>
                                        Actualiser
                                    </Button>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    {/* Tableau avec tri */}
                    <Card className="border-0 shadow-sm rounded-3 overflow-hidden">
                        <Card.Header className="bg-transparent border-0 pt-4 pb-3">
                            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                                <div className="d-flex align-items-center gap-2">
                                    <div className="section-icon-small">
                                        <i className="ri-group-line text-primary"></i>
                                    </div>
                                    <h6 className="mb-0 fw-semibold">Classement des clients</h6>
                                </div>
                                <Badge className="px-3 py-2 rounded-pill" style={{ background: '#21ACCD' }}>
                                    {indexOfFirstItem + 1}-{Math.min(indexOfLastItem, sortedData.length)} sur {sortedData.length}
                                </Badge>
                            </div>
                        </Card.Header>
                        <Card.Body className="p-0">
                            <div className="table-responsive">
                                <Table hover className="align-middle mb-0 custom-table">
                                    <thead className="bg-light">
                                        <tr>
                                            <th className="ps-4" style={{ width: '70px' }}>#</th>
                                            <th className="sortable" onClick={() => handleSort('raison_sociale')}>
                                                Client
                                                {sortConfig.key === 'raison_sociale' && (
                                                    <i className={`ri-arrow-${sortConfig.direction === 'asc' ? 'up' : 'down'}-s-line ms-1`}></i>
                                                )}
                                            </th>
                                            <th className="text-end sortable" onClick={() => handleSort('nb_factures')}>
                                                Factures
                                                {sortConfig.key === 'nb_factures' && (
                                                    <i className={`ri-arrow-${sortConfig.direction === 'asc' ? 'up' : 'down'}-s-line ms-1`}></i>
                                                )}
                                            </th>
                                            <th className="text-end sortable" onClick={() => handleSort('total_achats')}>
                                                Total achats
                                                {sortConfig.key === 'total_achats' && (
                                                    <i className={`ri-arrow-${sortConfig.direction === 'asc' ? 'up' : 'down'}-s-line ms-1`}></i>
                                                )}
                                            </th>
                                            <th className="text-end sortable" onClick={() => handleSort('panier_moyen')}>
                                                Panier moyen
                                                {sortConfig.key === 'panier_moyen' && (
                                                    <i className={`ri-arrow-${sortConfig.direction === 'asc' ? 'up' : 'down'}-s-line ms-1`}></i>
                                                )}
                                            </th>
                                            <th className="sortable" onClick={() => handleSort('dernier_achat')}>
                                                Dernier achat
                                                {sortConfig.key === 'dernier_achat' && (
                                                    <i className={`ri-arrow-${sortConfig.direction === 'asc' ? 'up' : 'down'}-s-line ms-1`}></i>
                                                )}
                                            </th>
                                            <th className="text-center pe-4">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {currentItems.map((client, index) => {
                                            const globalIndex = indexOfFirstItem + index;
                                            const medalColor = globalIndex === 0 ? '#f59e0b' : globalIndex === 1 ? '#94a3b8' : globalIndex === 2 ? '#cd7f32' : '#64748b';
                                            const part = totalAchats > 0 ? ((parseFloat(client.total_achats) || 0) / totalAchats * 100).toFixed(1) : 0;
                                            
                                            return (
                                                <tr key={client.id_client} className="client-row">
                                                    <td className="ps-4">
                                                        <span className="ranking-badge" style={{ background: medalColor }}>
                                                            {globalIndex + 1}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <div className="d-flex align-items-center gap-2">
                                                            <div className="avatar-icon">
                                                                <i className="ri-user-3-line"></i>
                                                            </div>
                                                            <div>
                                                                <Link to={`/ventes/clientsDetails/${client.id_client}`} className="table-link">
                                                                    {client.raison_sociale}
                                                                </Link>
                                                                <div className="d-flex gap-2">
                                                                    <small className="text-muted">{client.code_client}</small>
                                                                    {client.telephone && (
                                                                        <small className="text-muted">
                                                                            <i className="ri-phone-line me-1 fs-10"></i>
                                                                            {client.telephone}
                                                                        </small>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td className="text-end">
                                                        <Badge className="rounded-pill px-3" style={{ background: '#e2f3f5', color: '#FFF' }}>
                                                            {client.nb_factures}
                                                        </Badge>
                                                    </td>
                                                    <td className="text-end fw-bold">
                                                        <span className="text-primary">{formatCurrency(client.total_achats)}</span>
                                                        <div className="progress-bar-custom mt-1" style={{ width: '80px', marginLeft: 'auto' }}>
                                                            <div className="progress-fill" style={{ width: `${parseFloat(part)}%`, background: '#21ACCD' }}></div>
                                                        </div>
                                                    </td>
                                                    <td className="text-end fw-semibold text-success">
                                                        {formatCurrency(client.panier_moyen)}
                                                    </td>
                                                    <td>
                                                        <span className="text-muted">
                                                            <i className="ri-calendar-line me-1 fs-10"></i>
                                                            {formatDate(client.dernier_achat)}
                                                        </span>
                                                    </td>
                                                    <td className="text-center pe-4">
                                                        <OverlayTrigger placement="top" overlay={<Tooltip>Voir le client</Tooltip>}>
                                                            <Button 
                                                                variant="outline-info" 
                                                                size="sm"
                                                                className="action-btn"
                                                                onClick={() => navigate(`/ventes/clientsDetails/${client.id_client}`)}
                                                            >
                                                                <i className="ri-eye-line"></i>
                                                            </Button>
                                                        </OverlayTrigger>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                        {currentItems.length === 0 && (
                                            <tr>
                                                <td colSpan="7" className="text-center py-5">
                                                    <i className="ri-inbox-line fs-48 text-muted mb-3 d-block"></i>
                                                    <h5>Aucune donnée disponible</h5>
                                                    <p className="text-muted">Aucun client actif sur cette période</p>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            </div>
                        </Card.Body>
                        
                        {/* Pagination */}
                        {totalPages > 1 && (
                            <div className="d-flex justify-content-between align-items-center p-3 border-top">
                                <div className="text-muted small">
                                    Affichage de {indexOfFirstItem + 1} à {Math.min(indexOfLastItem, sortedData.length)} sur {sortedData.length} clients
                                </div>
                                <Pagination className="mb-0">
                                    <Pagination.Prev onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} />
                                    {renderPagination()}
                                    <Pagination.Next onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} />
                                </Pagination>
                            </div>
                        )}
                    </Card>

                    {/* Pied de tableau avec totaux */}
                    {sortedData.length > 0 && (
                        <Card className="border-0 shadow-sm rounded-3 mt-3" style={{ background: '#f8fafc' }}>
                            <Card.Body className="p-3">
                                <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                                    <div className="d-flex align-items-center gap-2 text-muted small">
                                        <i className="ri-information-line fs-5" style={{ color: '#21ACCD' }}></i>
                                        <span>
                                            <strong>Total {getPeriodeLabel()}</strong> : {sortedData.length} clients actifs
                                        </span>
                                    </div>
                                    <div className="d-flex gap-4">
                                        <span className="fw-bold text-primary">{formatCurrency(totalAchats)}</span>
                                        <span className="fw-bold" style={{ color: '#21ACCD' }}>{formatNumber(totalFactures)}</span>
                                        <span className="fw-bold text-success">{formatCurrency(moyenneAchats)}</span>
                                    </div>
                                </div>
                            </Card.Body>
                        </Card>
                    )}
                </div>
                <Footer />
            </div>

            <style>{`
                /* KPI Cards */
                .kpi-card {
                    background: white;
                    border-radius: 0px;
                    padding: 1rem;
                    display: flex;
                    align-items: center;
                    gap: 1rem;
                    box-shadow: 0 1px 3px rgba(0,0,0,0.05);
                    transition: all 0.2s ease;
                    height: 100%;
                    border-left: 2px solid;
                }
                .kpi-card:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 8px 20px rgba(0,0,0,0.08);
                }
                .kpi-primary { border-left-color: #21ACCD; }
                .kpi-info { border-left-color: #0ea5e9; }
                .kpi-success { border-left-color: #10b981; }
                
                .kpi-icon {
                    width: 48px;
                    height: 48px;
                    border-radius: 12px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }
                .kpi-primary .kpi-icon { background: #e2f3f5; color: #21ACCD; }
                .kpi-info .kpi-icon { background: #e2f3f5; color: #0ea5e9; }
                .kpi-success .kpi-icon { background: #ecfdf5; color: #10b981; }
                
                .kpi-content {
                    flex: 1;
                }
                .kpi-label {
                    font-size: 0.7rem;
                    text-transform: uppercase;
                    color: #6c757d;
                    font-weight: 600;
                    letter-spacing: 0.5px;
                }
                .kpi-value {
                    font-size: 1.3rem;
                    font-weight: 700;
                    margin: 0.25rem 0;
                    color: #1a3c5e;
                }
                .kpi-unit {
                    font-size: 0.7rem;
                    color: #6c757d;
                }
                
                /* Ranking Badge */
                .ranking-badge {
                    width: 36px;
                    height: 36px;
                    border-radius: 50%;
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    font-weight: 700;
                    font-size: 0.9rem;
                    color: white;
                }
                
                /* Avatar Icon */
                .avatar-icon {
                    width: 40px;
                    height: 40px;
                    background: #e2f3f5;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: #21ACCD;
                }
                .avatar-icon i {
                    font-size: 1.2rem;
                }
                
                /* Section Icon Small */
                .section-icon-small {
                    width: 32px;
                    height: 32px;
                    background: linear-gradient(135deg, #21ACCD20, #1a8ba820);
                    border-radius: 10px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }
                
                /* Table Link */
                .table-link {
                    color: #21ACCD;
                    text-decoration: none;
                    font-weight: 600;
                }
                .table-link:hover {
                    text-decoration: underline;
                }
                
                /* Action Buttons */
                .action-btn {
                    width: 32px;
                    height: 32px;
                    border-radius: 8px;
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    transition: all 0.2s ease;
                    border: 1px solid #e2e8f0;
                    background: white;
                    color: #64748b;
                }
                .action-btn:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 4px 8px rgba(0,0,0,0.1);
                }
                .btn-outline-info:hover {
                    background: linear-gradient(135deg, #0891b2, #0e7490);
                    border-color: #0891b2;
                    color: white;
                }
                
                /* Progress Bar */
                .progress-bar-custom {
                    background: #e2e8f0;
                    border-radius: 4px;
                    height: 4px;
                    overflow: hidden;
                }
                .progress-fill {
                    height: 100%;
                    border-radius: 4px;
                    transition: width 0.3s ease;
                }
                
                /* Custom Table */
                .custom-table thead th {
                    background: #f8fafc;
                    color: #1e293b;
                    font-weight: 600;
                    font-size: 0.75rem;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                    padding: 1rem 0.75rem;
                    border-bottom: 2px solid #e2e8f0;
                    white-space: nowrap;
                }
                .sortable {
                    cursor: pointer;
                    user-select: none;
                }
                .sortable:hover {
                    color: #21ACCD;
                    background: #f1f5f9;
                }
                
                .custom-table tbody td {
                    padding: 0.75rem;
                    vertical-align: middle;
                    border-bottom: 1px solid #f1f5f9;
                }
                
                .client-row {
                    transition: all 0.2s ease;
                }
                
                .client-row:hover {
                    background: #f8fafc;
                }
                
                /* Pagination */
                .pagination {
                    gap: 5px;
                }
                .pagination .page-item .page-link {
                    border-radius: 8px;
                    border: 1px solid #e2e8f0;
                    color: #1e293b;
                    padding: 0.5rem 1rem;
                    transition: all 0.2s ease;
                    font-weight: 500;
                }
                .pagination .page-item.active .page-link {
                    background: linear-gradient(135deg, #21ACCD, #1a8ba8);
                    border-color: #21ACCD;
                    color: white;
                    box-shadow: 0 4px 10px rgba(33, 172, 205, 0.3);
                }
                .pagination .page-item .page-link:hover {
                    background: #f1f5f9;
                    transform: translateY(-2px);
                    box-shadow: 0 4px 8px rgba(0,0,0,0.1);
                }
                .pagination .page-item.disabled .page-link {
                    opacity: 0.5;
                    pointer-events: none;
                }
                
                .rounded-3 {
                    border-radius: 12px !important;
                }
                
                .rounded-pill {
                    border-radius: 50px !important;
                }
                
                .fs-10 {
                    font-size: 0.625rem;
                }
                .fs-20 {
                    font-size: 1.25rem;
                }
                .fs-24 {
                    font-size: 1.5rem;
                }
                .fs-48 {
                    font-size: 3rem;
                }
            `}</style>
        </>
    );
}